import { Image, Text, type UseModalState, useModalState } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { DialogActions, DialogContent, Stack } from "@mui/material";
import { FullScreenDialog } from "@src/appV2/lib/Dialogs";
import { Button } from "@src/appV2/redesign/components/Button";
import { PageHeaderTopRow } from "@src/appV2/redesign/components/PageHeaderTopRow";
import timeclockBiometricComplianceImage from "@src/appV2/Shifts/Shift/TakeClockActionPicture/images/timeclock_biometric_compliance.png";
import { EmphasizedText } from "@src/appV2/Shifts/Shift/TakeClockActionPicture/utils/EmphasizedText";

import { TimeclockHelpBottomSheet } from "./TimeclockHelpBottomSheet";
import { TimeclockInstructionImage } from "./TimeclockInstructionImage";

const TIMECLOCK_PIN_PLACEHOLDER = "{timeclockPin}";

interface VideoProofPolicyDialogProps {
  modalState: UseModalState;
  onContinue: (source: "main-dialog" | "help-dialog") => void;
  onCancel: () => void;
  workplaceName: string;
  clockAction: "clock-in" | "clock-out";
  timeclockLocation: string | undefined;
  timeclockPin: string | undefined;
  timeclockGifUrl: string | undefined;
  isSkipTimeclockUsageEnabled: boolean;
  timeClockUsageWarning: string;
}

export function VideoProofPolicyDialog(props: VideoProofPolicyDialogProps) {
  const {
    modalState,
    onContinue,
    onCancel,
    workplaceName,
    clockAction,
    timeclockLocation,
    timeclockPin,
    timeclockGifUrl,
    isSkipTimeclockUsageEnabled,
    timeClockUsageWarning,
  } = props;

  const helpModalState = useModalState("closed");

  // Check if the timeclockLocation contains the {timeclockPin} placeholder
  const locationContainsPinPlaceholder =
    isDefined(timeclockLocation) && timeclockLocation.includes(TIMECLOCK_PIN_PLACEHOLDER);

  // If the placeholder exists, substitute it with the actual pin value
  const processedTimeclockLocation =
    locationContainsPinPlaceholder && isDefined(timeclockPin)
      ? timeclockLocation.replace(TIMECLOCK_PIN_PLACEHOLDER, timeclockPin)
      : timeclockLocation;

  // Only show the separate PIN section if:
  // 1. There's a pin to show AND
  // 2. The location text doesn't already contain the pin placeholder
  const shouldShowSeparatePinSection = isDefined(timeclockPin) && !locationContainsPinPlaceholder;

  return (
    <>
      <FullScreenDialog modalState={modalState} variant="primary">
        <PageHeaderTopRow
          title={`Record your ${clockAction === "clock-in" ? "clock in" : "clock out"}`}
          sx={(theme) => ({
            "& .MuiTypography-root": {
              ...theme.typography.h4,
              fontWeight: theme.typography.fontWeightMedium,
              textAlign: "center",
              width: "100%",
            },
          })}
          onClickBack={onCancel}
        />
        <DialogContent sx={{ px: 7 }}>
          <Stack spacing={6} alignItems="stretch">
            {timeclockGifUrl ? (
              <TimeclockInstructionImage imageUrl={timeclockGifUrl} />
            ) : (
              <Image
                src={timeclockBiometricComplianceImage}
                alt="Time clock biometric compliance"
                width="100%"
              />
            )}
            <Text variant="body1">
              Please record a short video clearly showing yourself using the facility&apos;s time
              clock to complete your {clockAction === "clock-in" ? "clock-in" : "clock-out"}.
            </Text>
            <EmphasizedText text={processedTimeclockLocation ?? ""} />
            <Text variant="body1">
              Please ask the staff at {workplaceName} where the time clock is located if you cannot
              find it.
            </Text>

            {shouldShowSeparatePinSection ? (
              <>
                <Text variant="body1">To use the time clock, enter this code:</Text>
                <Text bold variant="h1" textAlign="center">
                  {timeclockPin}
                </Text>
              </>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ paddingBottom: 6 }}>
          <Stack spacing={3} direction="column" width="100%">
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                onContinue("main-dialog");
              }}
            >
              Let&apos;s do it!
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                helpModalState.openModal();
              }}
            >
              I need help
            </Button>
          </Stack>
        </DialogActions>
      </FullScreenDialog>
      <TimeclockHelpBottomSheet
        modalState={helpModalState}
        isSkipTimeclockUsageEnabled={isSkipTimeclockUsageEnabled}
        timeClockUsageWarning={timeClockUsageWarning}
        onCancel={helpModalState.closeModal}
        onContinue={() => {
          helpModalState.closeModal();
          if (isSkipTimeclockUsageEnabled) {
            onContinue("help-dialog");
          }
        }}
      />
    </>
  );
}
